import type { ActionPattern } from 'redux-saga/effects';
import { delay, getContext, race, take } from 'typed-redux-saga';
import type { SubscriptionFollowOutcome } from '../types/SubscriptionFollow.types';
import { SUBSCRIPTION_LOST_TOAST_KEY, SUBSCRIPTION_RETRY_MAX_DELAY_MS } from './constants';
import { getSubscriptionErrorMessage } from './getSubscriptionErrorMessage';
import { getSubscriptionRetryDelayMs } from './subscriptionRetry';
import { toast } from 'app/cross-cutting-concerns/toasts/kcc/toast';
import type { IDependencies } from 'app/cross-cutting-concerns/dependency-injection/interfaces/IDependencies';

interface FollowWithRetryOptions {
  /** Loads state, then follows the subscription; throws if loading fails. Rerun on every retry. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  follow: () => Generator<unknown, SubscriptionFollowOutcome, any>;
  stop: ActionPattern;
  /** Runs if loading fails before anything has loaded. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onErrorBeforeFirstLoad?: (error: unknown) => Generator<unknown, void, any>;
}

// AppSync never resubscribes after a dropped socket, so this reruns `follow` with backoff until `stop`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* followWithRetry({
  follow,
  stop,
  onErrorBeforeFirstLoad,
}: FollowWithRetryOptions): Generator<unknown, void, any> {
  // Failed attempts in a row.
  let failures = 0;
  // A subscription only fails after its state was loaded.
  let hasLoaded = false;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const startedAt = Date.now();

    try {
      const outcome = yield* follow();

      if (outcome.kind === 'stopped') {
        return;
      }

      hasLoaded = true;

      // Stayed connected longer than the longest wait, so the backoff starts over.
      const stayedConnected = Date.now() - startedAt >= SUBSCRIPTION_RETRY_MAX_DELAY_MS;

      if (stayedConnected) {
        failures = 0;
      }

      // AppSync closes its idle socket after the last unsubscribe, so a subscribe right after it fails once.
      // Only report a drop of a live subscription or a failure that repeats; the shared key keeps one toast.
      if (stayedConnected || failures === 1) {
        const { t } = yield* getContext<IDependencies>('dependencies');

        toast.error({
          key: SUBSCRIPTION_LOST_TOAST_KEY,
          message: t('robotCleaningControl.connectionLost.error') as string,
          description: getSubscriptionErrorMessage(outcome.error),
        });
      }
    } catch (error) {
      console.error(error);

      if (!hasLoaded && onErrorBeforeFirstLoad) {
        yield* onErrorBeforeFirstLoad(error);
      }
    }

    const { stopped } = yield* race({
      retry: delay(getSubscriptionRetryDelayMs(failures)),
      stopped: take(stop),
    });

    if (stopped) {
      return;
    }

    failures += 1;
  }
}
